import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { 
  BookOpen, Rocket, Coins, Vote, Radio, Shield, Zap, 
  ArrowRight, ExternalLink, Code, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";

const guides = [
  {
    icon: Rocket,
    title: "Getting Started",
    description: "Connect wallet, navigate the platform, and make your first trade",
    sections: ["Wallet Setup", "Platform Overview", "Your First Trade", "Security Tips"]
  },
  {
    icon: Coins,
    title: "Launching Tokens",
    description: "Create and launch your own meme coin with bonding curves",
    sections: ["Token Creation", "Bonding Curves", "Marketing Tips", "Best Practices"]
  },
  {
    icon: Vote,
    title: "Voting System",
    description: "Understand community-driven token locking and governance",
    sections: ["How Voting Works", "Lock vs Unlock", "Vote Weight", "Streamer Multipliers"]
  },
  {
    icon: Radio,
    title: "Streamer Integration",
    description: "For streamers: verification, multipliers, and best practices",
    sections: ["Getting Verified", "Vote Multipliers", "Disclosure Rules", "Monetization"]
  },
  {
    icon: Shield,
    title: "Safety & Security",
    description: "Protect yourself from scams and trade safely",
    sections: ["Identifying Scams", "DYOR Guide", "Wallet Security", "Reporting"]
  },
  {
    icon: Code,
    title: "API Documentation",
    description: "Build integrations and bots using our API",
    sections: ["Authentication", "Endpoints", "Webhooks", "Rate Limits"]
  },
];

const quickLinks = [
  { title: "What is a bonding curve?", href: "#bonding-curve" },
  { title: "How do fees work?", href: "#fees" },
  { title: "What happens when a token is locked?", href: "#locking" },
  { title: "How to become a verified streamer?", href: "#verification" },
  { title: "API rate limits", href: "#rate-limits" },
];

export default function Docs() {
  return (
    <>
      <Helmet>
        <title>Documentation - TheMoonRun</title>
        <meta name="description" content="Learn how to use TheMoonRun. Guides, tutorials, and API documentation." />
      </Helmet>
      
      <PageLayout>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Documentation</h1>
            <p className="text-muted-foreground">Learn everything about TheMoonRun</p>
          </div>
        </div>

              {/* Search */}
              <div className="p-6 rounded-xl bg-gradient-to-r from-primary/10 to-accent/5 border border-primary/20 mb-8">
                <h2 className="text-lg font-semibold mb-2">How can we help?</h2>
                <div className="flex gap-4">
                  <input 
                    type="text"
                    placeholder="Search documentation..."
                    className="flex-1 px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button>Search</Button>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-4">Popular Questions</h2>
                <div className="flex flex-wrap gap-2">
                  {quickLinks.map((link) => (
                    <a 
                      key={link.title}
                      href={link.href}
                      className="px-3 py-1.5 rounded-full bg-secondary text-sm hover:bg-secondary/80 transition-colors"
                    >
                      {link.title}
                    </a>
                  ))}
                </div>
              </div>

              {/* Guides Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {guides.map((guide) => (
                  <div key={guide.title} className="p-6 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <guide.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold">{guide.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{guide.description}</p>
                    <ul className="space-y-2 mb-4">
                      {guide.sections.map((section) => (
                        <li key={section} className="flex items-center gap-2 text-sm">
                          <ArrowRight className="w-3 h-3 text-primary" />
                          {section}
                        </li>
                      ))}
                    </ul>
                    <Button variant="ghost" className="w-full justify-between">
                      Read Guide
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Bonding Curve Section */}
              <section id="bonding-curve" className="mb-12">
                <h2 className="text-xl font-semibold mb-4">Understanding Bonding Curves</h2>
                <div className="p-6 rounded-xl bg-card border border-border">
                  <p className="text-muted-foreground mb-4">
                    A bonding curve is a mathematical formula that determines token price based on supply. 
                    As more tokens are bought, the price increases along the curve. When tokens are sold, 
                    the price decreases.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <Zap className="w-5 h-5 text-primary mb-2" />
                      <h4 className="font-medium mb-1">Instant Liquidity</h4>
                      <p className="text-xs text-muted-foreground">Always able to buy/sell</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <Shield className="w-5 h-5 text-success mb-2" />
                      <h4 className="font-medium mb-1">No Rug Pulls</h4>
                      <p className="text-xs text-muted-foreground">Liquidity locked in curve</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <Users className="w-5 h-5 text-warning mb-2" />
                      <h4 className="font-medium mb-1">Fair Launch</h4>
                      <p className="text-xs text-muted-foreground">No presales or VCs</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <strong>Formula:</strong> Price = Base Price × (1 + Supply / Curve Steepness)²
                  </p>
                </div>
              </section>

              {/* Fees Section */}
              <section id="fees" className="mb-12">
                <h2 className="text-xl font-semibold mb-4">Platform Fees</h2>
                <div className="p-6 rounded-xl bg-card border border-border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-2xl font-mono font-bold text-primary">1%</p>
                      <p className="text-sm text-muted-foreground">Trading Fee (buy/sell)</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-2xl font-mono font-bold text-primary">0.5 SOL</p>
                      <p className="text-sm text-muted-foreground">Token Launch Fee</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    Priority fees are optional and go directly to Solana validators for faster processing.
                  </p>
                </div>
              </section>

              {/* CTA */}
              <div className="p-8 rounded-2xl bg-gradient-to-r from-primary/20 to-accent/10 border border-primary/20 text-center">
                <h2 className="text-2xl font-display font-bold mb-2">Still have questions?</h2>
                <p className="text-muted-foreground mb-6">
                  Our support team is here to help 24/7
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Button asChild>
                    <a href="/support">Contact Support</a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="https://discord.com" target="_blank" rel="noopener noreferrer">
                      Join Discord
                    </a>
                  </Button>
                </div>
              </div>
      </PageLayout>
    </>
  );
}
